import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiArrowLeft,
    FiDownload,
    FiPieChart,
    FiAlertTriangle,
    FiPhone,
    FiCheckCircle,
    FiTrendingDown,
    FiTrendingUp,
    FiUsers,
} from 'react-icons/fi';
import { Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
} from 'chart.js';
import { useProfiles } from '../../hooks/useProfiles';
import {
    PageHeader,
    MetricCard,
    StatusBadge,
    EmptyState,
    SkeletonMetricCard,
    SkeletonChart,
    useToast,
} from '../../theme/components';
import { PageButton } from '../../theme/components/PageButton';
import Avatar from '../../theme/components/Avatar';
import { telLink } from '../../utils/format';
import { APP_NAME } from '../../config';
import { buildPdfReport } from '../../utils/pdfReport';
import {
    growthSeverity,
    GROWTH_STATUS,
} from '../../utils/growthStatus';
import './GrowthMonitoringReport.css';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale);

// Render an age label that switches unit based on how fresh the
// newborn is. Under 30 days the month calculation rounds to 0, which
// reads as "0 months" in the action queue — useless for triage on a
// newborn. Show days in that window; otherwise show whole months.
const formatChildAge = (dob) => {
    if (!dob) return '-';
    const birthDate = new Date(dob);
    const today = new Date();
    const msPerDay = 1000 * 60 * 60 * 24;
    // Strip the time-of-day so day-of-life is computed on whole days.
    const days = Math.floor(
        (new Date(today.toDateString()) - new Date(birthDate.toDateString())) /
            msPerDay,
    );
    if (days < 0) return '0 days';
    if (days < 30) return `${days} day${days === 1 ? '' : 's'}`;
    let months = (today.getFullYear() - birthDate.getFullYear()) * 12;
    months -= birthDate.getMonth();
    months += today.getMonth();
    if (today.getDate() < birthDate.getDate()) months -= 1;
    return `${Math.max(0, months)} months`;
};

const GrowthMonitoringReport = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { data: apiData, loading, error } = useProfiles();
    const childrenData = useMemo(() => apiData || [], [apiData]);

    // Per-bucket growth counts — each child counts in every bucket they
    // carry a status for (e.g. a child with [Severe Wasting, Underweight]
    // is counted in BOTH severeWasting AND underweight). This matches the
    // action queue pill stack at line 378-382 and the same per-bucket
    // semantics already used on VaccineSummaries. The display order
    // (ORDERED_ALERT_STATUSES) is independent of counting — it just
    // decides where each card lives visually on the page.
    const GROWTH_BUCKETS = [
        ['severeWasting', GROWTH_STATUS.SEVERE_WASTING],
        ['wasting', GROWTH_STATUS.WASTING],
        ['severeStunting', GROWTH_STATUS.SEVERE_STUNTING],
        ['stunting', GROWTH_STATUS.STUNTING],
        ['severelyUnderweight', GROWTH_STATUS.SEVERELY_UNDERWEIGHT],
        ['underweight', GROWTH_STATUS.UNDERWEIGHT],
        ['obese', GROWTH_STATUS.OBESE],
        ['overweight', GROWTH_STATUS.OVERWEIGHT],
        ['normal', GROWTH_STATUS.NORMAL],
    ];

    const stats = useMemo(() => {
        const s = Object.fromEntries(GROWTH_BUCKETS.map(([k]) => [k, 0]));
        childrenData.forEach((child) => {
            // growthStatuses is the new array field; fall back to the
            // legacy singular field so the report still renders on the
            // old API shape.
            const statuses = Array.isArray(child.growthStatuses)
                ? child.growthStatuses
                : (child.growthStatus ? [child.growthStatus] : []);
            // Per-bucket counting — a child carrying both Severe Wasting
            // AND Underweight counts in BOTH buckets, same as the action
            // queue pill stack renders (line 378-382). Mirrors the
            // VaccineSummaries fix: bucket totals reflect "how many
            // children have this indicator", not "how many children
            // have this as their most-severe indicator". The pie slices
            // still read correctly because they're sized by raw count,
            // and a child landing in multiple buckets makes the bucket
            // totals exceed childrenData.length (same trade-off the
            // user accepted on Vaccination Status).
            let counted = false;
            GROWTH_BUCKETS.forEach(([key, status]) => {
                if (statuses.includes(status)) {
                    s[key] += 1;
                    counted = true;
                }
            });
            if (!counted) s.normal += 1;
        });
        return s;
    }, [childrenData]);

    // Statuses that warrant follow-up (anything except Normal).
    const ALERT_STATUSES = GROWTH_BUCKETS
        .map(([, status]) => status)
        .filter((s) => s !== GROWTH_STATUS.NORMAL);

    // Display order on the page / chart / PDF. Grouped by indicator
    // with severe-first inside each group. Independent of the severity-
    // ordered bucket logic above (GROWTH_BUCKETS) which decides
    // which bucket a child counts in — here we just decide where the
    // row lives visually.
    const ORDERED_ALERT_STATUSES = [
        GROWTH_STATUS.SEVERELY_UNDERWEIGHT,
        GROWTH_STATUS.UNDERWEIGHT,
        GROWTH_STATUS.OBESE,
        GROWTH_STATUS.OVERWEIGHT,
        GROWTH_STATUS.SEVERE_WASTING,
        GROWTH_STATUS.WASTING,
        GROWTH_STATUS.SEVERE_STUNTING,
        GROWTH_STATUS.STUNTING,
    ];

    const DISPLAY_COLOR_BY_STATUS = {
        [GROWTH_STATUS.SEVERE_WASTING]: '#7f1d1d',
        [GROWTH_STATUS.WASTING]: '#ef4444',
        [GROWTH_STATUS.SEVERE_STUNTING]: '#6b21a8',
        [GROWTH_STATUS.STUNTING]: '#a855f7',
        [GROWTH_STATUS.SEVERELY_UNDERWEIGHT]: '#b91c1c',
        [GROWTH_STATUS.UNDERWEIGHT]: '#f87171',
        [GROWTH_STATUS.OBESE]: '#ea580c',
        [GROWTH_STATUS.OVERWEIGHT]: '#f59e0b',
        [GROWTH_STATUS.NORMAL]: '#10b981',
    };
    const PIE_COLORS = [
        ...ORDERED_ALERT_STATUSES.map((s) => DISPLAY_COLOR_BY_STATUS[s]),
        DISPLAY_COLOR_BY_STATUS[GROWTH_STATUS.NORMAL],
    ];

    const chartData = useMemo(
        () => ({
            labels: [...ORDERED_ALERT_STATUSES, GROWTH_STATUS.NORMAL],
            datasets: [
                {
                    data: [
                        ...ORDERED_ALERT_STATUSES.map((k) => stats[
                            GROWTH_BUCKETS.find(([, s]) => s === k)[0]
                        ] || 0),
                        stats.normal || 0,
                    ],
                    backgroundColor: PIE_COLORS,
                    borderWidth: 0,
                    hoverOffset: 8,
                },
            ],
        }),
        [stats],
    );

    const actionQueue = useMemo(
        () =>
            childrenData.filter((c) => {
                const statuses = Array.isArray(c.growthStatuses)
                    ? c.growthStatuses
                    : (c.growthStatus ? [c.growthStatus] : []);
                return statuses.some((s) => ALERT_STATUSES.includes(s));
            }),
        [childrenData],
    );

    const generatePDF = () => {
        const stamp = new Date().toISOString().split('T')[0];
        // Resolve per-child statuses once for the action queue rows.
        const resolveStatuses = (child) => {
            if (Array.isArray(child.growthStatuses) && child.growthStatuses.length > 0) {
                return child.growthStatuses;
            }
            return child.growthStatus ? [child.growthStatus] : [GROWTH_STATUS.UNKNOWN];
        };
        const distributionRows = [
            ...ORDERED_ALERT_STATUSES.map((label, i) => {
                const key = GROWTH_BUCKETS.find(([, s]) => s === label)[0];
                return [label, stats[key] || 0];
            }),
            ['Normal', stats.normal || 0],
        ];
        buildPdfReport({
            title: 'Growth Monitoring Summary Report',
            subtitle: `${APP_NAME} · Total Children: ${childrenData.length}`,
            filters: [`Growth view: all registered (${childrenData.length} assessed)`],
            sections: [
                {
                    heading: 'Growth Status Distribution',
                    head: ['Growth Status', 'Count', 'Percentage'],
                    body: distributionRows.map(([label, count]) => [
                        label,
                        count,
                        `${((count / Math.max(1, childrenData.length)) * 100).toFixed(1)}%`,
                    ]),
                    accent: [14, 165, 233],
                },
                {
                    heading: 'Action Queue (Underweight, Overweight, Wasting, Stunting)',
                    head: [
                        'Reg No',
                        'Child Name',
                        'Age',
                        'Status',
                        "Mother's Name",
                        'Contact',
                    ],
                    body: actionQueue.map((child) => [
                        child.childNo,
                        child.name,
                        formatChildAge(child.dob),
                        resolveStatuses(child).join(', '),
                        child.mothersName || '-',
                        child.contactNumber || '-',
                    ]),
                    accent: [239, 68, 68],
                },
            ],
            fileName: `Growth_Monitoring_Report_${stamp}.pdf`,
            toast,
        });
    };

    if (loading) {
        return (
            <div className="gmr-wrapper">
                <PageHeader title="Growth Monitoring Report" subtitle="Loading…" icon={FiPieChart} />
                <div className="gmr-body">
                    <div className="gmr-metrics">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <SkeletonMetricCard key={i} />
                        ))}
                    </div>
                    <div className="gmr-grid">
                        <SkeletonChart height={280} />
                        <div className="gmr-action-card">
                            <div className="gmr-action-card__title">Action Queue</div>
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="gmr-action-placeholder" />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="gmr-wrapper">
                <EmptyState
                    illustration="error"
                    title="Couldn't load report"
                    description={error}
                    action={
                        <PageButton type="button" variant="primary" onClick={() => window.location.reload()}>
                            Try again
                        </PageButton>
                    }
                />
            </div>
        );
    }

    return (
        <div className="gmr-wrapper">
            <PageHeader
                title="Growth Monitoring Report"
                subtitle="Overview of children's growth parameters and required follow-ups."
                icon={FiPieChart}
                actions={
                    <>
                        <PageButton type="button" variant="ghost" startIcon={<FiArrowLeft size={16} />} onClick={() => navigate('/reports')}>
                            Back
                        </PageButton>
                        <PageButton
                            type="button"
                            variant="primary"
                            startIcon={<FiDownload size={16} />}
                            onClick={generatePDF}
                            disabled={childrenData.length === 0}
                        >
                            Export PDF
                        </PageButton>
                    </>
                }
            />

            <div className="gmr-body">
                <div className="gmr-metrics">
                    <MetricCard
                        label="Total Children"
                        value={childrenData.length}
                        accent="primary"
                        icon={FiUsers}
                    />
                    <MetricCard label="Normal Growth" value={stats.normal} accent="success" icon={FiCheckCircle} />
                    <MetricCard
                        label="Severely Underweight"
                        value={stats.severelyUnderweight}
                        accent="error"
                        icon={FiTrendingDown}
                    />
                    <MetricCard
                        label="Underweight"
                        value={stats.underweight}
                        accent="error"
                        icon={FiTrendingDown}
                    />
                    <MetricCard
                        label="Obese"
                        value={stats.obese}
                        accent="warning"
                        icon={FiTrendingUp}
                    />
                    <MetricCard
                        label="Overweight"
                        value={stats.overweight}
                        accent="warning"
                        icon={FiTrendingUp}
                    />
                    <MetricCard
                        label="Severe Wasting"
                        value={stats.severeWasting}
                        accent="error"
                        icon={FiTrendingDown}
                    />
                    <MetricCard
                        label="Wasting"
                        value={stats.wasting}
                        accent="error"
                        icon={FiTrendingDown}
                    />
                    <MetricCard
                        label="Severe Stunting"
                        value={stats.severeStunting}
                        accent="error"
                        icon={FiTrendingDown}
                    />
                    <MetricCard
                        label="Stunting"
                        value={stats.stunting}
                        accent="error"
                        icon={FiTrendingDown}
                    />
                </div>

                <div className="gmr-grid">
                    <div className="gmr-panel">
                        <div className="gmr-panel__header">
                            <h3>Growth Distribution</h3>
                            <span className="gmr-panel__subtitle">
                                {childrenData.length} children total
                            </span>
                        </div>
                        <div className="gmr-chart-body">
                            <Pie data={chartData} options={{ maintainAspectRatio: false }} />
                        </div>
                    </div>

                    <div className="gmr-action-card">
                        <div className="gmr-action-card__header">
                            <h3>
                                <FiAlertTriangle size={16} /> Action Queue
                            </h3>
                            <StatusBadge severity="danger" dot>
                                {actionQueue.length} priority
                            </StatusBadge>
                        </div>
                        <div className="gmr-action-list">
                            {actionQueue.length > 0 ? (
                                actionQueue.map((child) => (
                                    <div key={child.id} className="gmr-action-item">
                                        <Avatar name={child.name} id={child.id || child.childNo} size={36} />
                                        <div className="gmr-action-item__info">
                                            <strong>{child.name}</strong>
                                            <div className="gmr-action-item__badges">
                                                {(() => {
                                                    const statuses = Array.isArray(child.growthStatuses)
                                                        ? child.growthStatuses
                                                        : (child.growthStatus ? [child.growthStatus] : []);
                                                    return statuses.map((s) => (
                                                        <StatusBadge key={s} severity={growthSeverity(s)}>
                                                            {s}
                                                        </StatusBadge>
                                                    ));
                                                })()}
                                            </div>
                                            <span className="gmr-action-item__sub">
                                                {formatChildAge(child.dob)}
                                            </span>
                                        </div>
                                        <div className="gmr-action-item__contact">
                                            <div className="gmr-action-item__mother">{child.mothersName || 'Mother'}</div>
                                            {child.contactNumber ? (
                                                <a href={telLink(child.contactNumber)} className="gmr-action-item__call">
                                                    <FiPhone size={12} /> {child.contactNumber}
                                                </a>
                                            ) : (
                                                <span className="gmr-action-item__call gmr-action-item__call--muted">
                                                    <FiPhone size={12} /> No contact
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <EmptyState
                                    illustration="no-data"
                                    compact
                                    title="Everyone is growing normally!"
                                    description="No follow-up growth assessments are needed right now."
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GrowthMonitoringReport;
